import React from 'react';
import QuestionPanel from "../../structure/QuestionPanel";
import RadioGroup from "../../structure/RadioGroup";
import {useFormikContext} from "formik";
import {DisabilityEmployerFormValues} from "../../disability/DisabilityEmployerFormValues";
import {PfmlEmployerFormValues} from "../../pfml/PfmlEmployerFormValues";
import {ConditionType} from "../../enums/ConditionType";
import SelectDropdown from "../../input/SelectDropdown";
import {LeaveType} from "../../enums/LeaveType";
import {useLeavePlans} from "../../../hooks/useLeavePlans";
import {DateTime} from "luxon";
import {buildClaimTypeOptions, filterLeaveTypesFor} from "../../domain/ClaimPlan";
import {Relationship} from "../../enums/Relationship";
import {DisabilityFormValues} from "../../disability/DisabilityFormValues";
import {WorkersCompensation} from "../../enums/WorkersCompensation";
import LoadingSpinner from "../../structure/LoadingSpinner";
import {useAuth} from "oidc-react";
import {BenefitType, findDefaultLeaveTypesFor} from "../../enums/BenefitType";

const Condition = () => {
    const { initialValues, values, setFieldValue } = useFormikContext<DisabilityEmployerFormValues | PfmlEmployerFormValues>()
    const auth = useAuth();
    const dateLastWorked = values.dateLastWorked;
    const effectiveDate = dateLastWorked ? DateTime.fromFormat(dateLastWorked, 'MM/dd/yyyy').plus({days: 1}).toISODate() : undefined;
    const { data: findLeavePlans, loading: leavePlansLoading } = useLeavePlans(values.policyNumber, (values as PfmlEmployerFormValues).workState, effectiveDate, auth)
    const relationship = values.relationship
    const leaveRelationship = Relationship.EMPLOYEE === relationship ? Relationship.EMPLOYEE : Relationship.FAMILY_MEMBER;
    const filteredLeaveTypes = filterLeaveTypesFor(findLeavePlans?.findLeavePlans, leaveRelationship)
    const leavePlans = filteredLeaveTypes?.length
        ? filteredLeaveTypes
        : filterLeaveTypesFor(findDefaultLeaveTypesFor(BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE), leaveRelationship)

    function findMemberIdentifiers() {
        return values.privacyId
            ? `Principal member ID: ${values.privacyId}`
            : `${values.firstName} ${values.lastName} (date of birth: ${values.dateOfBirth})`;
    }

    const workersCompensationSubject = encodeURIComponent(`Workers' Compensation - ER - ${values.claimId}`);
    const workersCompensationBody = encodeURIComponent(`Please see the attached workers' compensation letter for:\r\n${findMemberIdentifiers()}`);
    const neverWorkRelated = [LeaveType.MATERNITY_LEAVE, LeaveType.BONDING_WITH_A_CHILD, LeaveType.ACTIVE_DUTY_OR_MILITARY_EVENTS].includes((values as PfmlEmployerFormValues).leaveType)
        || ConditionType.PREGNANCY === (values as DisabilityEmployerFormValues).conditionType

    return (leavePlansLoading
            ? <LoadingSpinner spinnerText="Loading"/>
            : <QuestionPanel headerText="Describe your employee's condition."
                       detailsText="Depending on the reason for leave, we may need to check if workers' compensation applies.
                       You should file a workers' compensation claim if your employee's condition is due to a work-related illness or injury."
                       panelContent={
                           <div>
                               <SelectDropdown id="condition-type" label="Condition type" required={true}
                                               options={Object.keys(ConditionType).map(conditionType => ({
                                                   id: conditionType.replace(/_/g, '-').toLowerCase(),
                                                   text: (ConditionType as any)[conditionType]
                                               }))} changeCallback={(target: HTMLInputElement) => {
                                   if(ConditionType.PREGNANCY === target.value) {
                                       setFieldValue('leaveType', LeaveType.MATERNITY_LEAVE)
                                   } else if([ConditionType.SICKNESS, ConditionType.INJURY].includes(target.value as ConditionType)) {
                                       setFieldValue('leaveType', LeaveType.SERIOUS_HEALTH_CONDITION)
                                   }
                               }}/>
                               {(initialValues as DisabilityFormValues).conditionType === undefined
                                   && <SelectDropdown id="leave-type" label="Leave type" required={true} options={buildClaimTypeOptions(leavePlans ?? [])}/>}
                               { !neverWorkRelated &&
                                   <RadioGroup id="workers-compensation"
                                            label="Have you filed or are planning to file a workers' compensation claim for your employee?"
                                            required={true}
                                            radioProps={[
                                                { id: "not-applicable", text: WorkersCompensation.NOT_APPLICABLE, changeCallback: () => setFieldValue('workRelated', 'No') },
                                                { id: "filed-decision-pending", text: WorkersCompensation.FILED_DECISION_PENDING, changeCallback: () => setFieldValue('workRelated', 'Yes') },
                                                { id: "awarded", text: WorkersCompensation.AWARDED, changeCallback: () => setFieldValue('workRelated', 'Yes') },
                                                { id: "denied", text: WorkersCompensation.DENIED, changeCallback: () => setFieldValue('workRelated', 'Yes') }
                                            ]}/>
                               }
                               { ![WorkersCompensation.NOT_APPLICABLE, ''].includes(values.workersCompensation) &&
                                   <div id="workers-compensation-letter" className="pds-u-padding-bottom-16">
                                       If workers' compensation is approved or denied, please upload a copy of the award/denial letter to this claim or send it to <a href={`mailto:sbdclaims@principal.com?subject=${workersCompensationSubject}&body=${workersCompensationBody}.`}>sbdclaims@principal.com</a>.
                                   </div>
                               }
                           </div>}
        >
        </QuestionPanel>
    )
};

export default Condition;